import type { Chart, Tick } from 'chart.js'
import { point, feature } from '@turf/helpers'
import type { LineString, Position } from 'geojson'
import {
  convertToMeters,
  getConversionFunction,
  getUnitAbbreviation,
} from '@/components/shared/x-distance.vue'
import distance from '@turf/distance'
import length from '@turf/length'
/**
 * Get HSL values from slope
 *
 * @param endY Elevation of the further point
 * @param startY Elevation of the previous point
 * @param step Distance between points
 *
 * @returns Array containing HSL values
 */
export function slopeToColor(endY: number, startY: number, step: number) {
  const slope = Math.abs((endY - startY) / step)

  if (slope < 0.01) {
    return [100, '100%', '33%']
  }

  if (slope < 0.023) {
    return [91, '100%', '36%']
  }

  if (slope < 0.043) {
    return [82, '100%', '41%']
  }

  if (slope < 0.065) {
    return [73, '100%', '44%']
  }

  if (slope < 0.09) {
    return [64, '100%', '48%']
  }

  if (slope < 0.13) {
    return [60, '100%', '50%']
  }

  if (slope < 0.171) {
    return [40, '100%', '50%']
  }

  if (slope < 0.228) {
    return [27, '100%', '50%']
  }

  if (slope < 0.28) {
    return [13, '100%', '50%']
  }

  return [0, '100%', '50%']
}

/**
 * Get information about the element at a given point, used by the hover plugin to show
 * the elevation and distance at the hovered point.
 *
 * @param chart - the chart.js chart object
 * @param elevationProfile - elevationProfile LineString
 * @param elementIndex - the index of the element to find information for
 * @param distanceUnit - the unit to convert distance to
 * @param elevationUnit - the unit to convert elevation to
 * @returns the distance and elevation at the given point, and a geojson point representing
 *  its location on the map
 */
export function getInfoForForElementIndex(
  chart: Chart,
  elevationProfile: LineString,
  elementIndex: number,
  distanceUnit: string,
  elevationUnit: string
) {
  const value = chart.data.datasets[0].data[elementIndex] as {
    x: number
    y: number
  }

  const distance = getConversionFunction('meters')(distanceUnit)(value.x)
  const elevation = getConversionFunction('meters')(elevationUnit)(value.y)
  const pointOnMap = point(elevationProfile.coordinates[elementIndex])
  const isLastPoint = elementIndex === chart.data.datasets[0].data.length - 1
  const roundedDistance = isLastPoint
    ? Math.round(distance * 10) / 10
    : Math.floor(distance * 10) / 10
  const roundedElevation = Math.round(elevation)

  return { distance: roundedDistance, elevation: roundedElevation, pointOnMap }
}

/**
 * Get config info for the x axis
 *
 * @param distance the distance of the LineString in meters
 * @param unitType the unit to convert the distance to for label display logic
 * @returns the step size and the maximum value to use for the x axis
 */
export function getXAxisInfo(distance: number, unitType: string) {
  const convertedDistance = getConversionFunction('meters')(unitType)(distance)

  let step
  if (convertedDistance <= 0.8) {
    step = 0.1
  } else if (convertedDistance <= 2) {
    step = 0.2
  } else if (convertedDistance <= 4) {
    step = 0.5
  } else if (convertedDistance <= 8) {
    step = 1
  } else if (convertedDistance <= 16) {
    step = 2
  } else {
    step = Math.round(convertedDistance / 8)
  }

  return {
    xStep: convertToMeters(unitType, step),
    xMax: distance,
  }
}

/**
 * Get config info for the x axis
 *
 * @param minElevation: the minimum elevation in the elevationProfile LineString
 * @param maxElevation: the maximum elevation in the elevationProfile LineString
 * @returns the min, max, and step to use for the y axis
 */
export function getYAxisInfo(minElevation: number, maxElevation: number) {
  let yMin, yMax
  const diff = maxElevation - minElevation
  if (diff > 50) {
    // If the diff is over 50 meters, fit the graph to the min and max values
    yMin = minElevation
    yMax = maxElevation
  } else {
    // If the diff is less then 50 meters, center the content but make the yAxis
    // span 50 meters
    const center = minElevation + diff / 2
    yMin = center - 25
    yMax = center + 25
  }

  const yStep = (yMax - yMin) / 2

  return { yMin, yMax, yStep }
}

/**
 * Get the necessary data from the elevationProfile LineString to display the
 * elevation profile graph
 *
 * @param elevationProfile - elevationProfile LineString
 * @returns Data that can be displayed in chart.js, the minimum and maximum elevation in the
 * LineString, and the totalDistance of the LineString
 */
export function getElevationData(
  elevationProfile: LineString,
  actualDistance: number
) {
  let distanceAtPoint = 0
  let maxElevation = elevationProfile.coordinates[0][2]
  let minElevation = elevationProfile.coordinates[0][2]

  const elevationProfileDistance = length(feature(elevationProfile), {
    units: 'meters',
  })
  const multiplicator = actualDistance / elevationProfileDistance

  const data = elevationProfile.coordinates.map((pos: Position, index) => {
    if (index > 0) {
      const coord = point(pos)
      const previousCoord = elevationProfile.coordinates[index - 1]
      const distanceFromLastPoint = distance(previousCoord, coord, {
        units: 'meters',
      })
      distanceAtPoint += distanceFromLastPoint
    }

    const elevation = pos[2]
    if (elevation > maxElevation) {
      maxElevation = elevation
    }
    if (elevation < minElevation) {
      minElevation = elevation
    }

    // Note the points here are not necessarily evenly spaced which is why
    // we need to calculate the distance at each point for the graph to be accurate
    return { y: elevation, x: distanceAtPoint * multiplicator }
  })

  return { data, minElevation, maxElevation }
}

/**
 * The callback to be executed for the x tick label
 *
 * @param value - the value at the tick
 * @param index -  the index of the tick
 * @param ticks -  the ticks array
 * @param unit - the unit to convert the label to
 * @param xStep - the distance between each tick
 * @param totalDistance - the total distance of the elevationProfile LineString
 * @returns The label to display at the given tick
 */
export function xTickCallback(
  value: number,
  index: number,
  ticks: Tick[],
  unit: string,
  xStep: number,
  totalDistance: number
) {
  if (index === 0) {
    // Display the label for the first tick
    return getUnitAbbreviation(unit)
  } else if (index === ticks.length - 1) {
    // By default the last tick in chart.js will be at the
    // end of the x axis. Instead we want it to be evenly spaced
    // with the rest of the ticks and leave the end unmarked
    ticks.pop()
    const lastTick = ticks[ticks.length - 1]
    const value = lastTick.value + xStep
    const label = `${_getXLabel(value, totalDistance, unit)}`
    ticks.push({ label, value })
  }

  return _getXLabel(value, totalDistance, unit)
}

/**
 * Formats the label for an x tick
 * This should only be used by the xTickCallback function
 *
 * @param value - the value at the tick
 * @param totalDistance - the total distance of the elevationProfile LineString
 * @param unit - the unit to convert the label to
 * @returns The label to display at the given value
 */
const _getXLabel = (value: number, totalDistance: number, unit: string) => {
  const convertedDistance = getConversionFunction('meters')(unit)(totalDistance)
  const convertedValue = getConversionFunction('meters')(unit)(value)

  return convertedDistance < 4
    ? Math.round(convertedValue * 10) / 10
    : Math.round(convertedValue)
}

/**
 * The callback to be executed for the y tick label
 * @param value - the value at the tick
 * @param index -  the index of the tick
 * @param unit - the unit to convert the label to
 * @returns the label to display for the given tick
 */
export function yTickCallback(value: number, index: number, unit: string) {
  // Don't display anything at the bottom of the y axis
  if (index === 0) {
    return ''
  }

  const convertedElevation = Math.round(
    getConversionFunction('meters')(unit)(value)
  )
  let label = getUnitAbbreviation(unit)
  if (label === 'ft') {
    label = "'"
  }
  return convertedElevation + label
}

/**
 * Return the elevation gradient
 * @param ctx - the canvas context
 * @param canvas - the canvas html object
 * @param elevationProfile - the elevationProfile LineString
 * @param totalDistance - the total distance of the elevationProfile
 * @returns a linear gradient colored by the elevation at each point
 */
export function getElevationGradient(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  elevationProfile: LineString
) {
  const elevationGradient = ctx.createLinearGradient(0, 0, canvas.width, 0)
  elevationProfile.coordinates.forEach((coordinate, index) => {
    if (index !== elevationProfile.coordinates.length - 1) {
      const nextCoordinate = elevationProfile.coordinates[index + 1]
      const step = distance(nextCoordinate, coordinate, {
        units: 'meters',
      })
      const slopeColor = slopeToColor(nextCoordinate[2], coordinate[2], step)
      const ratio = (index + 1) / elevationProfile.coordinates.length
      elevationGradient.addColorStop(ratio, `hsla(${slopeColor.join(',')}, .5)`)
    }
  })

  return elevationGradient
}

/**
 * Return the gray gradient
 * @param ctx - the canvas context
 * @param canvas - the canvas html object
 * @returns a gray linear gradient
 */
export function getGrayGradient(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement
) {
  const greyGradient = ctx.createLinearGradient(0, canvas.height, 0, 0)
  // Add color stops
  greyGradient.addColorStop(1, 'rgba(51, 51, 51, 0.40)')
  greyGradient.addColorStop(0, 'rgba(51, 51, 51, 0)')

  return greyGradient
}

/**
 * Get information about the hovered chart element
 * @param mouseEvent - the event returned by onmousemove
 * @param chart - the chart.js chart object
 * @returns the closest chart element being hovered and its index, as well as
 * wether or not the graph is actually being hovered
 */
export function getHoverPluginElementInfo(
  mouseEvent: MouseEvent,
  chart: Chart
) {
  // Draw the hover text if the user is hovering the edges of the chart
  // where there is no elevation data
  const elements = chart.getElementsAtEventForMode(
    mouseEvent,
    'index',
    { intersect: false },
    false
  )
  const element = elements?.[0]?.element
  const elementIndex = elements?.[0]?.index

  const beforeFirstPoint =
    element && elementIndex === 0 && mouseEvent.offsetX < element.x - 1
  const afterLastPoint =
    element &&
    elementIndex === chart.data.datasets[0].data.length - 1 &&
    mouseEvent.offsetX > element.x + 1

  return {
    element,
    elementIndex,
    mouseOnGraph: !!element && !beforeFirstPoint && !afterLastPoint,
  }
}
